/**
 * Shared content module for onboarding projection web + PDF.
 * Single source of truth for citation-heavy sections (Strategic Context, Assumptions & Validation).
 * Both OnboardingProjection.tsx and generateProjectionReport.ts import from here.
 */

// ---- Segment types ----

export type Segment =
    | string
    | { bold: string }
    | { cite: string; url: string }
    | { link: string; url: string };

export type RichText = Segment[];

// ---- Content interfaces ----

export interface StrategicSection {
    title: string;
    body: RichText[];
}

export interface AssumptionItem {
    status: 'KEY LIMITATION' | 'ASSUMED' | 'OBSERVED' | 'QUALITATIVE';
    title: string;
    body: RichText[];
    callout?: RichText[];
}

export interface AssumptionContext {
    closeRate: number;
    generalConversion: number;
    policyValue: number;
    insuranceMargin: number;
    preneedRate: number;
    accountName: string;
    monthsInRange: number;
    signaturesPerMonth: number;
    targetAccounts: number;
    collectionRate: number;
}

// ---- Plain-text renderer (for PDF) ----

export function richTextToPlain(segments: RichText): string {
    return segments.map(segment => {
        if (typeof segment === 'string') return segment;
        if ('bold' in segment) return segment.bold;
        if ('cite' in segment) return `[${segment.cite}]`;
        if ('link' in segment) return segment.link;
        return '';
    }).join('');
}

// ---- Strategic Context ----

export function getStrategicContextSections(): StrategicSection[] {
    return [
        {
            title: "A centuries-old ritual meets new technology",
            body: [
                [
                    { bold: "Gathering to honor the dead is one of humanity's oldest rituals." },
                    " The funeral guestbook formalizes that impulse into a lasting record. While visitors' registers date to the 16th-century ",
                    { link: "album amicorum", url: "https://en.wikipedia.org/wiki/Album_amicorum" },
                    ", the funeral guest register became a standard practice in the 19th century alongside the professionalization of the funeral industry ",
                    { cite: "1", url: "https://miamioh.edu/cca/_files/mcguffey/documents/archive/mourning-and-memories.pdf" },
                    ". Research shows the physical act of writing activates broader brain connectivity than typing ",
                    { cite: "Van der Meer 2024", url: "https://www.frontiersin.org/journals/psychology/articles/10.3389/fpsyg.2023.1219945/full" },
                    ", and signing one's name specifically primes self-identity and increases identity-congruent behavior ",
                    { cite: "Kettle & Haubl 2011", url: "https://academic.oup.com/jcr/article/38/3/474/1807559" },
                    ".",
                ],
            ],
        },
        {
            title: "Digital alternatives and the AI inflection point",
            body: [
                [
                    "Since roughly 2012, digital alternatives have appeared in funeral homes — most notably ",
                    { link: "Expression Guest Registry", url: "https://www.messengerstationery.com/messenger-products/technology-solutions/expression-guest-registry" },
                    " (typed input on iPad, acquired by Messenger/Tribute Technology in 2013) and ",
                    { link: "A Simple Thank You", url: "https://asimplethankyou.com/digital-registry" },
                    " (launched at ICCFA 2012). These systems digitized the data but replaced the signing experience with typed keyboard input. More recent entrants like ",
                    { link: "FuneralScreen", url: "https://www.funeralscreen.com/digital-guest-registry/" },
                    " use QR codes and guests' own phones.",
                ],
                [
                    { bold: "Natural handwriting-to-structured-data transcription only became commercially viable in 2023-2024." },
                    " While basic handwriting-to-text existed for decades (Apple Newton 1993, Palm Graffiti 1997, Windows Tablet PC 2002 ",
                    { cite: "history", url: "https://en.wikipedia.org/wiki/Handwriting_recognition" },
                    "), the ability to have someone write naturally on a tablet and have AI parse that into structured data fields (name, email, city, state) — not just raw text — required multimodal vision-language models that reached production quality with GPT-4V (late 2023) ",
                    { cite: "first documented use cases", url: "https://aigenealogyinsights.com/2023/10/09/new-use-case-handwriting-recognition-to-structured-data/" },
                    " and subsequent models. Signature Guestbook is built on this convergence: attendees sign with a stylus, preserving the ritual significance, while the platform captures, transcribes, and structures the data.",
                ],
            ],
        },
        {
            title: "Structurally different lead quality",
            body: [
                [
                    { bold: "Lead quality is structurally different here." },
                    " The act of signing at a funeral service is a high-intent behavioral signal. The attendee is physically present at a moment when mortality and planning are top of mind. Research on life-event marketing shows people experiencing a life event are up to 25x more likely to purchase a relevant product ",
                    { cite: "Porch Group Media", url: "https://porchgroupmedia.com/blog/life-event-marketing-and-how-target-consumers-when-it-matters-most/" },
                    ". Funeral attendance is among the strongest mortality-awareness triggers available.",
                ],
            ],
        },
        {
            title: "Market position",
            body: [
                [
                    { bold: "We are not aware of any other platform that combines stylus-based signing with AI transcription for insurance lead generation in funeral services." },
                    " Expression Guest Registry offers a basic preneed interest checkbox ",
                    { cite: "ICCFA 2013", url: "https://connectingdirectors.com/39891-iccfa-2013-hands-on-with-expression-guest-registry" },
                    ", but uses typed input without AI transcription. Tribute Technology's ",
                    { link: "Lead Logic", url: "https://www.tributetech.com/lead-logic" },
                    " generates leads from website visitors, not from the guestbook interaction itself. With 1,700+ funeral tech startups globally, we cannot rule out all competitors, but this specific combination appears to be unique.",
                ],
            ],
        },
        {
            title: "Why this matters for the numbers in this report",
            body: [
                [
                    "The projections that follow are built on observed data from funeral homes already using the platform. The preneed interest rates are directly measured from production data. The conversion rates are assumptions (see Assumptions & Validation) — but the lead pipeline itself is real, captured at scale, at minimal marginal cost, from a context no other channel can replicate. The opportunity modeled here is the result of scaling this proven interaction pattern across a larger funeral home network.",
                ],
            ],
        },
    ];
}

// ---- Assumptions & Validation ----

export function getAssumptionItems(ctx: AssumptionContext): AssumptionItem[] {
    return [
        {
            status: 'KEY LIMITATION',
            title: 'No End-to-End Policy Tracking Exists Yet',
            body: [
                [
                    `There is currently no integration that tracks a guestbook signer through to an issued insurance policy. The platform captures signatures and preneed interest expressions — that data is observed and verified. But the downstream conversion rates (${ctx.closeRate}% close rate, ${ctx.generalConversion}% general conversion) are assumptions, not measured outcomes. No system exists today to confirm how many leads generated by the guestbook result in written policies. Building that closed-loop tracking would be a prerequisite for validating the conversion assumptions in this report.`,
                ],
            ],
            callout: [
                [
                    { bold: "What the platform does deliver:" },
                    " high-quality, high-intent leads captured at the exact moment people are thinking about end-of-life planning — at minimal marginal cost per lead. The guestbook is already deployed in funeral homes for its primary function (memorial signatures). Lead capture is a byproduct of that existing workflow, not a separate acquisition cost. The value proposition to an insurance partner is the lead pipeline itself — delivered efficiently, at scale, from a context no other channel can replicate. The conversion rates in this report model what that pipeline could be worth, but the actual value will depend on the insurance partner's own sales process and close rates.",
                ],
            ],
        },
        {
            status: 'ASSUMED',
            title: `${ctx.closeRate}% Close Rate on Preneed-Interested Leads`,
            body: [
                [
                    "This rate applies only to attendees who ",
                    { bold: "already indicated preneed interest" },
                    ` while signing the guestbook at a funeral — not cold leads. These are people who just attended a funeral service, physically signed a guestbook, and proactively expressed interest in planning ahead. Industry data on warm insurance lead conversion varies widely by channel: referral leads close at ~60%, live transfers at ~20%, and inbound search leads at ~15% `,
                    { cite: "Agents Alliance", url: "https://agentsalliance.com/warm-leads-how-much-better-are-they/" },
                    `. Guestbook preneed leads fall somewhere between referral and search — physically present, self-selected, but not personally referred. The ${ctx.closeRate}% assumption sits in the middle of this range. The sensitivity analysis models 20% and 50% as lower and upper bounds. This assumption has not been validated at scale with production close rate tracking data.`,
                ],
            ],
        },
        {
            status: 'ASSUMED',
            title: `${ctx.generalConversion}% General Conversion Rate`,
            body: [
                [
                    "This applies to funeral attendees who did ",
                    { bold: "not" },
                    ` express preneed interest but are still in a mortality-aware context. ${ctx.generalConversion}% means that out of every 100 general attendees captured by the guestbook, ${ctx.generalConversion} eventually purchase a policy through downstream marketing. For comparison, direct mail campaigns typically convert at 0.5-2% `,
                    { cite: "Invesp", url: "https://www.invespcro.com/blog/the-average-direct-mail-conversion-rate-and-the-best-tips-to-improve-it/" },
                    ", while life-event-triggered marketing can be up to 25x more effective than general outreach ",
                    { cite: "Porch Group Media", url: "https://porchgroupmedia.com/blog/life-event-marketing-and-how-target-consumers-when-it-matters-most/" },
                    ". Funeral attendance is among the strongest mortality-awareness triggers. The sensitivity analysis models 0.5% through 3%.",
                ],
            ],
        },
        {
            status: 'OBSERVED',
            title: `${(ctx.preneedRate * 100).toFixed(1)}% Preneed Interest Rate`,
            body: [
                [
                    `This is directly observed from production data — the percentage of guestbook signers who indicate preneed interest. The rate is derived from the median account (${ctx.accountName}) over ${ctx.monthsInRange.toFixed(0)} months. Using the median rather than the mean provides an outlier-resistant baseline. The sample reflects the top accounts by volume, which have the most statistically significant per-account data. A larger account sample would increase confidence but the current rate is observed behavior, not an assumption.`,
                ],
            ],
        },
        {
            status: 'ASSUMED',
            title: `$${ctx.policyValue.toLocaleString()} Average Policy Face Value`,
            body: [
                [
                    "LIMRA's 2024 Life Insurers Council survey reports an average single-premium preneed policy of $5,398 ",
                    { cite: "LIMRA 2024", url: "https://www.limra.com/en/newsroom/news-releases/2025/2025-life-insurers-council-survey-indicates-continued-growth-in-preneed-market/" },
                    ". NFDA's 2023 General Price List Study reports median funeral costs of $8,300 for burial and $6,280 for cremation ",
                    { cite: "NFDA 2023", url: "https://nfda.org/news/media-center/nfda-news-releases/id/8134/2023-nfda-general-price-list-study-shows-inflation-increasing-faster-than-the-cost-of-a-funeral" },
                    `. $${ctx.policyValue.toLocaleString()} sits in the range between the LIMRA single-premium average and the NFDA burial cost. Preneed policies can be purchased as a single lump-sum payment or via monthly installments over 5-10 years. The model uses the face value as the starting point and applies a collection rate (below) to account for the payment mix and lapse risk. Actual policy values would also vary by region, services selected, and inflation protection riders.`,
                ],
            ],
        },
        {
            status: 'ASSUMED',
            title: `${ctx.collectionRate}% Premium Collection Rate`,
            body: [
                [
                    `Not all policy face value is collected as premium. Preneed insurance is sold both as single-premium (full value collected up front) and as installment plans (monthly payments over 5-10 years). With installment plans, some policyholders lapse — they stop paying before the policy is fully funded. The ${ctx.collectionRate}% collection rate is a blended estimate: it assumes a mix of single-premium buyers (who pay in full) and installment buyers (some of whom will lapse), resulting in ${ctx.collectionRate}% of face value actually collected as premium on average. This discounts the $${ctx.policyValue.toLocaleString()} face value to an effective premium of $${Math.round(ctx.policyValue * ctx.collectionRate / 100).toLocaleString()} per policy. Industry preneed lapse rates vary but are generally lower than other life insurance products due to the strong price-lock motivation.`,
                ],
            ],
        },
        {
            status: 'ASSUMED',
            title: `${ctx.insuranceMargin}% Insurance Margin`,
            body: [
                [
                    `This margin figure is a simplified proxy for the total economic return to the insurer per policy, not a verified underwriting margin. Published financial data from preneed-focused carriers (e.g., Security Plan Life, Homesteaders Life, NGL) shows company-level net profit margins in the single-digit to low-teens range. However, preneed economics are unusual: premiums are collected years or decades before claims are paid, generating substantial investment income during the holding period. When investment income, policy lapse gains (policyholders who cancel), and premium loading are included, the total economic return per policy can significantly exceed the company-level profit margin. The ${ctx.insuranceMargin}% figure in this report approximates that total per-policy economic return, not the company's bottom-line margin. This report does not include independent actuarial verification. The sensitivity analysis models 15%, 25%, and 35% to show the range under different assumptions about total economic return.`,
                ],
            ],
        },
        {
            status: 'OBSERVED',
            title: `${ctx.signaturesPerMonth.toFixed(1)} Signatures/Month (Median Account)`,
            body: [
                [
                    `Directly measured from production data. The median is used instead of the mean to avoid skew from high-volume or low-volume outliers. This rate reflects the throughput of an established account — newly onboarded accounts may take time to reach this level (modeled by the adoption ramp setting). Seasonal variation in funeral service volume is not explicitly modeled but is averaged over the ${ctx.monthsInRange.toFixed(0)}-month observation period.`,
                ],
            ],
        },
        {
            status: 'QUALITATIVE',
            title: 'Competitive Positioning Claims',
            body: [
                [
                    "The competitive landscape described in Strategic Context is based on identified competitors: ",
                    { link: "Expression Guest Registry", url: "https://www.messengerstationery.com/messenger-products/technology-solutions/expression-guest-registry" },
                    " (typed input, preneed checkbox; acquired by Tribute Technology 2013), ",
                    { link: "A Simple Thank You", url: "https://asimplethankyou.com/digital-registry" },
                    " (launched ICCFA 2012), and ",
                    { link: "FuneralScreen", url: "https://www.funeralscreen.com/digital-guest-registry/" },
                    ". No formal competitive analysis or third-party validation is included. With 1,700+ funeral tech startups globally, we cannot rule out all competitors, but the specific combination of stylus-based signing + AI transcription for insurance lead generation appears to be unique. These claims describe the current competitive landscape as understood by the company and may change as the market evolves.",
                ],
            ],
        },
        {
            status: 'ASSUMED',
            title: `Sustainability Across ${ctx.targetAccounts.toLocaleString()} Accounts`,
            body: [
                [
                    `The projection assumes that per-account metrics (signatures/month, preneed rate) hold as the platform scales from the current account base to ${ctx.targetAccounts.toLocaleString()} accounts. In practice, later-adopting funeral homes may differ from early adopters in volume, engagement, or market characteristics. The S-curve onboarding model accounts for gradual rollout but does not model degradation of per-account performance at scale. This is an optimistic assumption that should be revisited as the account base grows.`,
                ],
            ],
        },
    ];
}
